import { useEffect, useState } from 'react';
import { useLocalStorage } from 'hooks/use-local-storage';
import { useSearch } from 'hooks/use-search';
import { queueMacrotask } from 'utils/macrotask';

interface UseCurrentViewParams {
  defaultViewId: number;
}
interface UseCurrentViewResult {
  viewId: number;
  setViewId: (viewId: number, replace?: boolean) => void;
}
interface UseCurrentViewWithLocalStorageParams {
  defaultViewId: number;
  loading: boolean;
  localStorageKey: string;
}

type UseCurrentViewFunction = (
  params: UseCurrentViewParams,
) => UseCurrentViewResult;

export const useCurrentView: UseCurrentViewFunction = ({ defaultViewId }) => {
  const [viewId, setViewId] = useSearch<number>({
    from: (parsed) => parseInt((parsed.viewId ?? defaultViewId).toString(), 10),
    to: (id: number) =>
      id === defaultViewId ? { viewId: null } : { viewId: id.toString() },
  });

  return {
    viewId,
    setViewId,
  };
};

export const useCurrentViewWithLocalStorage = ({
  defaultViewId,
  loading,
  localStorageKey,
}: UseCurrentViewWithLocalStorageParams): UseCurrentViewResult => {
  const { viewId, setViewId } = useCurrentView({ defaultViewId });

  const [storedViewId, saveViewId] = useLocalStorage(
    localStorageKey,
    defaultViewId.toString(),
  );

  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (loading || !initialized) {
      return;
    }
    saveViewId(viewId.toString());
  }, [viewId, loading, saveViewId, initialized]);

  useEffect(() => {
    if (loading || initialized) {
      return;
    }
    if (viewId === defaultViewId && viewId !== parseInt(storedViewId, 10)) {
      queueMacrotask(() => setViewId(parseInt(storedViewId, 10), true));
    }
    setInitialized(true);
  }, [viewId, defaultViewId, loading, setViewId, storedViewId, initialized]);

  return {
    viewId,
    setViewId,
  };
};
